import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Center, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { ProfessionalPicture } from '~/commonComponents';
import { getJobStatus } from '~/components/Dashboard/ReceiptList/utils';
import {
  IGetOnsiteInterviewListViewEntity,
  OnsiteInterviewStatus,
  OnsiteInterviewStatusMapper,
} from '~/domain/PermanentJobs';
import { SolidUserIcon } from '@tempmee/react-icons';
import { JobStatusEnum } from '~/models';
import { firePermInterviewListInterviewClicked } from '~/posthogs/events';

interface IListItemProps {
  data: IGetOnsiteInterviewListViewEntity;
  jobPostingId: string;
}

export const ListItem = ({
  data: {
    id,
    localDate,
    localTimeEndsAt,
    localTimeStartsAt,
    professional,
    rate,
    status,
    totalCandidatesCount,
  },
  jobPostingId,
}: IListItemProps) => {
  const navigate = useNavigate();

  const fmtStatus = getJobStatus(status);

  const handleRowClick = useCallback(() => {
    // Track interview click event
    const onsiteInterviewStatus = OnsiteInterviewStatusMapper.fromJobStatus(status);
    firePermInterviewListInterviewClicked({
      postingId: jobPostingId,
      interviewId: id,
      status: onsiteInterviewStatus,
    });

    switch (status) {
      case JobStatusEnum.Counter:
      case JobStatusEnum.Open:
      case JobStatusEnum.Pending:
        navigate(`/dashboard/job/${id}`);
        break;
      default:
        navigate(`/dashboard/job/${id}/details`);
    }
  }, [id, navigate, status, jobPostingId]);

  const Candidate = useMemo(() => {
    const onsiteInterviewStatus = OnsiteInterviewStatusMapper.fromJobStatus(status);

    if (onsiteInterviewStatus === OnsiteInterviewStatus.ActionNeeded) {
      return (
        <Row align="center" justify="center">
          <SolidUserIcon size={24} color={colors.secondary[500]} />
          <Typography as="span" bold color={colors.secondary[500]} size="heading_4" style={{ marginTop: 6 }}>
            {totalCandidatesCount > 5 ? '5+' : totalCandidatesCount}
          </Typography>
        </Row>
      );
    }

    if (
      [OnsiteInterviewStatus.Scheduled, OnsiteInterviewStatus.Completed].includes(
        onsiteInterviewStatus,
      )
    ) {
      return (
        <Center>
          <ProfessionalPicture
            imageUrl={professional?.imageUrl || ''}
            name={professional?.fullName || ''}
          />
        </Center>
      );
    }

    return <Row />;
  }, [professional?.fullName, professional?.imageUrl, status, totalCandidatesCount]);

  return (
    <Box
      className="grid items-center cursor-pointer shadow-xs hover__shadow-sm bg-shades-0 rounded-field"
      data-cy="onsite-interview-list-item"
      height="80px"
      onClick={handleRowClick}
      style={{ gridTemplateColumns: '25% 25% 8% 20% 22%' }}
    >
      <Typography as="span" bold variant="body1" style={{ paddingLeft: 36 }}>
        {localDate}
      </Typography>
      <Typography as="span">
        {localTimeStartsAt} - {localTimeEndsAt}
      </Typography>
      <Typography as="span">{`$${rate.toFixed(2)}`}</Typography>
      {Candidate}
      <Typography as="span" color={fmtStatus.color} variant="body1" style={{ paddingRight: 36 }}>
        {fmtStatus.label}
      </Typography>
    </Box>
  );
};
